import * as React from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import Layout from "@/Layout";
import { useNavigation } from "@/utils";
import { navigationPolicy } from "@/navigation/policy";
import type { RouteShellProps } from "../../navigation.types";
import "./RouteShell.scss";

/** 与 RouteShell.scss 的 transition 时长一致（motion token --duration-slow） */
const TRANSITION_MS = 280;
/** 边缘右滑返回：起点距左缘 < 20px、位移 > 60px */
const EDGE_START_PX = 20;
const EDGE_SWIPE_PX = 60;

type Direction = "none" | "push" | "pop";

const isTabRoot = (pathname: string) =>
  navigationPolicy.tabRootRoutes.includes(pathname);

/**
 * 路由壳 template2（supergol / Ionic 观感）：
 *   · Layout 常驻（头部 + 底栏只在 Tab 根页渲染，二级页隐藏），Tab ↔ 二级页不再整壳重挂；
 *   · 二级页 iOS 式转场：push 从右滑入（旧页左移 30% 压暗），pop 镜像；Tab 根页之间、replace 跳转无动画；
 *   · 每个页面层自滚动（position:absolute + overflow），旧层在退场期间继续渲染旧 location；
 *   · 左缘右滑返回（非交互式：松手后按 pop 动画回退）。
 */
export const RouteShell: React.FC<RouteShellProps> = ({ renderRoutes }) => {
  const location = useLocation();
  const navType = useNavigationType();
  const { goBack } = useNavigation();

  // 方向在 location 变化的那次渲染里决定一次并钉住（key 相同的重复渲染——含 StrictMode 双渲染——不再重算）
  const lastRef = React.useRef<{ key: string; path: string; dir: Direction }>({
    key: location.key,
    path: location.pathname,
    dir: "none",
  });
  if (lastRef.current.key !== location.key) {
    const prev = lastRef.current.path;
    const next = location.pathname;
    let dir: Direction = "none";
    if (prev !== next && navType !== "REPLACE") {
      // 竞品（Ionic md）：底栏 Tab 之间切换也是「新页自下 40px 上滑淡入、旧页静止」，与二级页进入同一动画；
      // 只有二级页的返回（POP）才用镜像的下滑淡出
      const tabSwitch = isTabRoot(prev) && isTabRoot(next);
      dir = navType === "POP" && !tabSwitch ? "pop" : "push";
    }
    lastRef.current = { key: location.key, path: next, dir };
  }
  const direction = lastRef.current.dir;
  const timeout = direction === "none" ? 0 : TRANSITION_MS;

  // CSSTransition 的 nodeRef（避免 findDOMNode）：按 location.key 各持一个 ref，退场完成后回收
  const layerRefs = React.useRef(
    new Map<string, React.RefObject<HTMLDivElement>>()
  );
  const layerRef = (key: string) => {
    let ref = layerRefs.current.get(key);
    if (!ref) {
      ref = React.createRef<HTMLDivElement>();
      layerRefs.current.set(key, ref);
    }
    return ref;
  };

  // 边缘右滑返回
  const touchStartX = React.useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    const x = e.touches[0]?.clientX ?? 0;
    touchStartX.current = x < EDGE_START_PX ? x : null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - start;
    if (dx > EDGE_SWIPE_PX && !isTabRoot(location.pathname)) goBack();
  };

  const chrome = isTabRoot(location.pathname);
  // 下拉刷新在本壳里恒关：Layout 的 PullToRefresh 会按 canRefresh 换包裹结构（首页有壳、其它页裸渲染），
  // Tab 之间切换时整棵子树被重挂，转场动画就没了；而且页面层自滚动，外层的下拉判定也不可靠（竞品首页亦无下拉刷新）
  return (
    <Layout cannotRefresh chrome={chrome}>
      <div
        className="route-shell"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <TransitionGroup
          component={null}
          // 退场层沿用「进场时」决定的方向会出错（它是上一次的），统一按本次方向克隆
          childFactory={child =>
            React.cloneElement(child, {
              classNames: `route-${direction}`,
              timeout,
            })
          }
        >
          <CSSTransition
            key={location.key}
            nodeRef={layerRef(location.key)}
            classNames={`route-${direction}`}
            timeout={timeout}
            onExited={() => layerRefs.current.delete(location.key)}
          >
            <div ref={layerRef(location.key)} className="route-layer">
              {renderRoutes(location)}
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </Layout>
  );
};

export default RouteShell;
